import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import { API_CONSTANTS } from '../../utils/constants';
import { logout } from '../user/profileSlice';

import type { MenuItem, MenuResponse, MenuState } from './model';

const initialState: MenuState = {
  menuItems: [],
  filteredMenus: [],
  loading: false,
  error: null,
  isInitialized: false,
};

export const fetchMenu = createAsyncThunk<
  MenuResponse,
  void,
  { rejectValue: string }
>('menu/fetchMenu', async (_, { rejectWithValue, dispatch }) => {
  try {
    const authToken = localStorage.getItem('access_token');
    const response = await fetch(
      API_CONSTANTS.BASE_URL + API_CONSTANTS.MENU_ENDPOINT,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        dispatch(logout());
        window.location.href = '/';
      }
      throw new Error('Failed to fetch menu data');
    }

    const data: MenuResponse = await response.json();

    return data;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Unknown error occurred');
  }
});

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setFilteredMenus: (state, action: PayloadAction<MenuItem[]>) => {
      state.filteredMenus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchMenu.fulfilled,
        (state, action: PayloadAction<MenuResponse>) => {
          state.loading = false;
          state.isInitialized = true;
          state.menuItems = action.payload.data;
          state.filteredMenus = action.payload.data;
        }
      )
      .addCase(fetchMenu.rejected, (state, action) => {
        state.loading = false;
        state.isInitialized = true;
        state.error = action.payload ?? 'Unknown error occurred';
      });
  },
});

export const { setFilteredMenus } = menuSlice.actions;
export default menuSlice;
